import { Inject, Injectable } from '@angular/core';
import { ActionDefinition, isLifecycleAction, LifecycleActionDefinition } from '../model/widget-action.model';
import type { BaseWidget } from '../model/widget-base.model';
import type { WidgetState } from '../model/widget-config.model';
import { WIDGET_FACTORY, type WidgetFactory } from '../model/widget-factory.model';

/**
 * Command to execute on a widget. Used by AI tutors or external controllers.
 */
export interface WidgetCommand {
  widgetId: string;
  /** Lifecycle action OR ActionDefinition for widget actions */
  action: LifecycleActionDefinition | ActionDefinition;
}

// Result of executing a widget command.
export interface WidgetActionResult {
  widgetId: string;
  success: boolean;
  action: string; // action that was executed
  data?: unknown; // Result data (if any)
  error?: string; // Error message (if failed)
}

// Widget information for listing.
export interface WidgetInfo {
  id: string;
  type: string;
}

// Single instance across entire application: Singleton on root level via injectable.
// Shared widget registry: all widgets from all pages/components are tracked in one Map<string, BaseWidget>
// Global access: any component can inject the orchestrator and control any registered widget
@Injectable()
export class WidgetOrchestratorService {
  // Inject multiple WidgetFactory instances (multi-provider)
  constructor(@Inject(WIDGET_FACTORY) private readonly factories: WidgetFactory[]) {}

  // Runtime widget registry with ID and instances
  private readonly widgets = new Map<string, BaseWidget>();

  // Register a widget instance for orchestration
  registerWidget(id: string, widget: BaseWidget): void {
    if (this.widgets.has(id)) {
      console.warn(`Widget with id '${id}' is already registered. Overwriting.`);
    }
    this.widgets.set(id, widget);
  }

  // Unregister a widget instance.
  unregisterWidget(id: string): void {
    this.widgets.delete(id);
  }

  // Get a widget instance by ID.
  getWidget(id: string): BaseWidget | null {
    return this.widgets.get(id) || null;
  }

  /**
   * Execute an ActionDefinition directly on a widget.
   */
  async dispatchAction(widgetId: string, action: ActionDefinition): Promise<WidgetActionResult> {
    return this.executeCommand({ widgetId, action });
  }

  /**
   * Execute a sequence of commands in order.
   * Stops on first error unless continueOnError is true.
   */
  async executeSequence(commands: WidgetCommand[], continueOnError = false): Promise<WidgetActionResult[]> {
    const results: WidgetActionResult[] = [];

    for (const command of commands) {
      const result = await this.executeCommand(command);
      results.push(result);

      if (!result.success && !continueOnError) {
        break;
      }
    }

    return results;
  }

  /**
   * Execute multiple commands in parallel.
   * Useful for coordinated animations across widgets.
   */
  async executeParallel(commands: WidgetCommand[]): Promise<WidgetActionResult[]> {
    const promises = commands.map((command) => this.executeCommand(command));
    return Promise.all(promises);
  }

  // Get current state of a widget.
  getWidgetState(id: string): WidgetState | null {
    const widget = this.widgets.get(id);
    return widget ? widget.getState() : null;
  }

  // List all registered widgets.
  listWidgets(): WidgetInfo[] {
    const widgets: WidgetInfo[] = [];

    this.widgets.forEach((widget, id) => {
      widgets.push({
        id,
        type: widget.type,
      });
    });

    return widgets;
  }

  // Check if a widget is registered.
  has(id: string): boolean {
    return this.widgets.has(id);
  }

  // Get count of registered widgets.
  count(): number {
    return this.widgets.size;
  }

  // Clear all registered widgets. Useful for cleanup or testing.
  clear(): void {
    this.widgets.clear();
  }

  /**
   * Execute a single command on a widget.
   */
  private async executeCommand(command: WidgetCommand): Promise<WidgetActionResult> {
    const widget = this.widgets.get(command.widgetId);
    const actionName = command.action.action;

    if (!widget) {
      return {
        success: false,
        widgetId: command.widgetId,
        action: actionName,
        error: `Widget '${command.widgetId}' not found`,
      };
    }

    try {
      const { action } = command;

      // ActionDefinition path - widget actions
      if (!isLifecycleAction(action)) {
        const result = await widget.executeAction(action);
        return {
          success: result.success,
          widgetId: command.widgetId,
          action: action.action,
          error: result.success ? undefined : result.error,
        };
      }

      // Lifecycle actions
      let data: unknown;

      switch (action.action) {
        case 'init':
          widget.destroy();
          widget.init(action.payload);
          break;

        case 'update':
          widget.update(action.payload);
          break;

        case 'setState':
          widget.setState(action.payload);
          break;

        case 'getState':
          data = widget.getState();
          break;
      }

      return {
        success: true,
        widgetId: command.widgetId,
        action: action.action,
        data,
      };
    } catch (error) {
      return {
        success: false,
        widgetId: command.widgetId,
        action: actionName,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }
}
